import { useQuery } from '@tanstack/react-query';
import { microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useState } from 'react';
import { useDebounce } from 'react-use';
import { QUERY_KEYS } from '../../queryKeys';
import { getMyGroupsGraphApiQuery } from './getMyGroupsGraphApiQuery';

export type EntraGroup = { displayName: string; id: string };

export const useEntraGroups = (startsWith: string, minimumLength: number) => {
  const GRAPH_SCOPE_GROUPMEMBER_READ_ALL = 'https://graph.microsoft.com/GroupMember.Read.All';
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const [debounced, setDebounced] = useState(startsWith);

  const DEBOUNCE_DELAY_MS = 500;

  useDebounce(
    () => {
      setDebounced(startsWith);
    },
    DEBOUNCE_DELAY_MS,
    [startsWith],
  );

  const getEntraGroups = async (): Promise<EntraGroup[]> => {
    const entraUserToken = await microsoftAuth.getAccessToken([GRAPH_SCOPE_GROUPMEMBER_READ_ALL]);
    const { url, options } = getMyGroupsGraphApiQuery(debounced, entraUserToken);
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`Failed to fetch groups: ${response.statusText}`);
    }
    const data = await response.json();
    return data.value;
  };

  const {
    data: entraGroups,
    isLoading,
    error,
  } = useQuery({
    queryKey: QUERY_KEYS.scaffolder.entraGroups(debounced),
    queryFn: getEntraGroups,
    enabled: debounced.length >= minimumLength,
  });

  return { entraGroups, isLoading, error };
};
